type UnauthorizedHandler = () => void;

const unauthorizedHandlers = new Set<UnauthorizedHandler>();

export function registerUnauthorizedHandler(handler: UnauthorizedHandler) {
  unauthorizedHandlers.add(handler);
  return () => {
    unauthorizedHandlers.delete(handler);
  };
}

function notifyUnauthorizedHandlers() {
  unauthorizedHandlers.forEach((handler) => {
    try {
      handler();
    } catch (error) {
      console.error('Unauthorized handler failed', error);
    }
  });
}

export type ApiRequestOptions = Omit<RequestInit, 'body'> & {
  query?: Record<string, string | number | boolean | null | undefined>;
  body?: unknown;
  ignoreUnauthorized?: boolean;
};

export class ApiError extends Error {
  public readonly status: number;
  public readonly details: unknown;

  constructor(status: number, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

const API_PREFIX = '/api/v1';

const defaultHeaders = {
  'Content-Type': 'application/json'
};

function buildUrl(path: string, query?: Record<string, string | number | boolean | null | undefined>): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const url = new URL(API_PREFIX + normalizedPath, 'http://placeholder');
  if (query) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null) continue;
      params.append(key, String(value));
    }
    const queryString = params.toString();
    if (queryString) {
      url.search = queryString;
    }
  }
  return url.pathname + url.search;
}

export async function apiFetch<TResponse>(path: string, options: ApiRequestOptions = {}): Promise<TResponse> {
  const { query, body, headers, ignoreUnauthorized, ...rest } = options;

  const requestInit: RequestInit = {
    credentials: 'include',
    ...rest,
    headers: {
      ...defaultHeaders,
      ...(headers ?? {})
    }
  };

  if (body !== undefined) {
    if (body instanceof FormData || body instanceof Blob || body instanceof ArrayBuffer || ArrayBuffer.isView(body)) {
      // Non-JSON body – let fetch handle headers automatically.
      delete (requestInit.headers as Record<string, string>)['Content-Type'];
      requestInit.body = body as BodyInit;
    } else if (typeof body === 'string') {
      requestInit.body = body;
    } else {
      requestInit.body = JSON.stringify(body);
    }
  }

  const url = buildUrl(path, query);
  const response = await fetch(url, requestInit);

  if (response.status === 204) {
    return undefined as TResponse;
  }

  let data: unknown = undefined;
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    data = await response.json();
  } else {
    const text = await response.text();
    data = text;
  }

  if (!response.ok) {
    if (response.status === 401 && !ignoreUnauthorized) {
      notifyUnauthorizedHandlers();
    }
    const message =
      typeof data === 'object' && data !== null && 'error' in data
        ? String((data as Record<string, unknown>).error)
        : response.statusText || 'Request failed';
    throw new ApiError(response.status, message, data);
  }

  return data as TResponse;
}

export function getJson<TResponse>(path: string, options: ApiRequestOptions = {}) {
  return apiFetch<TResponse>(path, { ...options, method: options.method ?? 'GET' });
}

export function postJson<TResponse>(path: string, body?: unknown, options: ApiRequestOptions = {}) {
  return apiFetch<TResponse>(path, { ...options, method: 'POST', body });
}

export function putJson<TResponse>(path: string, body?: unknown, options: ApiRequestOptions = {}) {
  return apiFetch<TResponse>(path, { ...options, method: 'PUT', body });
}

export function deleteJson<TResponse>(path: string, options: ApiRequestOptions = {}) {
  return apiFetch<TResponse>(path, { ...options, method: 'DELETE' });
}

